import type { ReactNode, RefObject } from 'react'
import { useEffect, useState, useCallback } from 'react'

import { Popover, SimpleSpinner, useTheme } from '@buffer-mono/popcorn'

import { useEmojiMartLoader, type EmojiObject } from './useEmojiMartLoader'

import styles from './EmojiPicker.module.css'

const FALLBACK_EMOJI = '❓'

interface EmojiPickerCssProperties extends React.CSSProperties {
  '--background-rgb': string
  '--rgb-background': string
  '--rgb-accent': string
}

/**
 * The emoji-mart library requires rgb values for the background and accent colors.
 * Once we can use the Popcorn theme variables, use the them directly.
 *
 * - background-rgb: var(--color-bg-surface-floating)
 * - rgb-background: var(--color-bg-surface-floating)
 * - rgb-accent: var(--color-text-brand)
 */
const THEME_STYLES: Record<string, Partial<EmojiPickerCssProperties>> = {
  vrDark: {
    '--background-rgb': '30 31 31',
    '--rgb-background': '30 31 31',
    '--rgb-accent': '51 112 70',
  },
  dark: {
    '--background-rgb': '48 48 48',
    '--rgb-background': '48 48 48',
    '--rgb-accent': '27 48 185',
  },
  light: {
    '--background-rgb': '255 255 255',
    '--rgb-background': '255 255 255',
    '--rgb-accent': '27 48 185',
  },
  vrLight: {
    '--background-rgb': '255 255 255',
    '--rgb-background': '255 255 255',
    '--rgb-accent': '51 112 70',
  },
} as const

const LoadingEmojiPicker = (): JSX.Element => {
  return (
    <div className={styles.container}>
      <SimpleSpinner size="medium" />
    </div>
  )
}

const ErrorEmojiPicker = (): JSX.Element => {
  return (
    <div className={styles.container}>
      <span>Failed to load emoji picker</span>
    </div>
  )
}

export const EmojiPicker = ({
  onEmojiSelect,
  emojiPickerRef,
  children,
}: {
  onEmojiSelect: (emoji: string) => void
  emojiPickerRef: RefObject<HTMLDivElement>
  children: ReactNode
}): JSX.Element => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { loadingState, loadModules } = useEmojiMartLoader()
  const { theme } = useTheme()
  const isDarkMode = theme === 'dark' || theme === 'vrDark'

  // Initialize emoji-mart when modules are ready and popover is open
  useEffect(() => {
    if (isOpen && loadingState.status === 'ready' && !isInitialized) {
      void loadingState.modules.init({ data: loadingState.modules.data })
      setIsInitialized(true)
    }
  }, [isOpen, loadingState, isInitialized])

  const handleOpenChange = useCallback(
    (open: boolean): void => {
      setIsOpen(open)
      if (open) {
        void loadModules()
      }
    },
    [loadModules],
  )

  const handleEmojiSelect = useCallback(
    (emojiObject: EmojiObject): void => {
      const emojiString = emojiObject.native || FALLBACK_EMOJI
      onEmojiSelect(emojiString)
      setIsOpen(false)
    },
    [onEmojiSelect],
  )

  const renderPickerContent = (): JSX.Element => {
    switch (loadingState.status) {
      case 'loading':
        return <LoadingEmojiPicker />

      case 'error':
        return <ErrorEmojiPicker />

      case 'ready': {
        const { Picker, data } = loadingState.modules
        return (
          <Picker
            previewEmoji="point_up"
            perLine={9}
            data={data}
            theme={isDarkMode ? 'dark' : 'light'}
            onEmojiSelect={handleEmojiSelect}
          />
        )
      }

      default:
        return <LoadingEmojiPicker />
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        {children}
      </Popover.Trigger>
      <Popover.Content
        ref={emojiPickerRef}
        size="medium"
        arrow
        className={styles.content}
      >
        <div
          data-tip="Add Emoji"
          style={THEME_STYLES[theme] || THEME_STYLES.light}
        >
          {renderPickerContent()}
        </div>
      </Popover.Content>
    </Popover>
  )
}
